import React, { useState, useEffect, useMemo } from 'react';
import { History, Filter, X, RefreshCw, Undo2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = '#32B4AC';

const SOURCE_LABELS = {
  automation_campaigns: 'Automation Analytics',
  campaigns_data: 'Campaign Analytics',
};

const AuditLogView = ({ showToast }) => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ sources: [], users: [], total: 0 });
  const [loading, setLoading] = useState(true);

  // Filter state
  const [source, setSource] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [rowIdFilter, setRowIdFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    loadSummary();
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSummary = async () => {
    try {
      const res = await fetch(`${API}/audit-log/summary`, {
        headers: { 'X-User-Id': user?.id || '' },
      });
      if (!res.ok) throw new Error('summary fetch failed');
      setSummary(await res.json());
    } catch (e) {
      // Non-fatal
    }
  };

  const loadEntries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (source) params.append('source', source);
      if (userEmail) params.append('user_email', userEmail);
      if (rowIdFilter) params.append('row_id', rowIdFilter);
      if (fromDate) params.append('from_date', new Date(fromDate).toISOString());
      if (toDate) {
        // Include the entire "to" day: set to 23:59:59.999
        const t = new Date(toDate);
        t.setHours(23, 59, 59, 999);
        params.append('to_date', t.toISOString());
      }
      const url = `${API}/audit-log${params.toString() ? `?${params}` : ''}`;
      const res = await fetch(url, { headers: { 'X-User-Id': user?.id || '' } });
      if (!res.ok) throw new Error('audit fetch failed');
      const data = await res.json();
      setEntries(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast?.('Failed to load audit log', 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSource('');
    setUserEmail('');
    setRowIdFilter('');
    setFromDate('');
    setToDate('');
    setTimeout(loadEntries, 0);
  };

  const hasActiveFilter = source || userEmail || rowIdFilter || fromDate || toDate;

  const [revertingId, setRevertingId] = useState(null);
  const handleRevert = async (entry) => {
    if (entry.reverted) {
      showToast?.('This entry was already reverted', 'error');
      return;
    }
    if (entry.revert) {
      showToast?.('You cannot revert a revert entry', 'error');
      return;
    }
    const label = entry.row_summary || entry.row_id;
    const fieldList = (entry.changes || []).map((c) => c.field).join(', ');
    const ok = window.confirm(
      `Roll back "${label}"?\n\n` +
      `Fields to revert: ${fieldList}\n\n` +
      `This will restore the values that existed BEFORE this edit was made. ` +
      `The rollback itself will be recorded in the audit log.`
    );
    if (!ok) return;
    setRevertingId(entry.id);
    try {
      const res = await fetch(`${API}/audit-log/${entry.id}/revert`, {
        method: 'POST',
        headers: { 'X-User-Id': user?.id || '' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Revert failed');
      }
      showToast?.('Row reverted to previous values', 'success');
      loadSummary();
      loadEntries();
    } catch (e) {
      showToast?.(e.message || 'Failed to revert', 'error');
    } finally {
      setRevertingId(null);
    }
  };

  const formatValue = (v) => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  const grouped = useMemo(() => {
    // Group entries by date (YYYY-MM-DD) for readability
    const byDate = {};
    entries.forEach((e) => {
      const d = e.timestamp ? e.timestamp.slice(0, 10) : 'Unknown';
      if (!byDate[d]) byDate[d] = [];
      byDate[d].push(e);
    });
    return Object.entries(byDate).sort((a, b) => b[0].localeCompare(a[0]));
  }, [entries]);

  return (
    <div className="space-y-4" data-testid="audit-log-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <History size={18} className="text-slate-500" /> Audit Log
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            All raw-data edits across Automation Analytics and Campaign Analytics — {summary.total} entries total.
          </p>
        </div>
        <button
          onClick={loadEntries}
          data-testid="audit-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <Filter size={12} /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Source</label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              data-testid="audit-filter-source"
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm"
            >
              <option value="">All sources</option>
              {summary.sources.map((s) => (
                <option key={s} value={s}>{SOURCE_LABELS[s] || s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Edited by</label>
            <select
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              data-testid="audit-filter-user"
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm"
            >
              <option value="">All users</option>
              {summary.users.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Row ID</label>
            <input
              type="text"
              value={rowIdFilter}
              onChange={(e) => setRowIdFilter(e.target.value)}
              data-testid="audit-filter-rowid"
              placeholder="Optional…"
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="audit-filter-from"
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="audit-filter-to"
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadEntries}
            data-testid="audit-apply"
            style={{ backgroundColor: TEAL }}
            className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg hover:opacity-90"
          >
            Apply filters
          </button>
          {hasActiveFilter && (
            <button
              onClick={clearFilters}
              data-testid="audit-clear"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg"
            >
              <X size={11} /> Clear
            </button>
          )}
          <span className="ml-auto text-xs text-slate-500 tabular-nums">
            Showing {entries.length} entries
          </span>
        </div>
      </div>

      {/* Entries */}
      {loading ? (
        <div className="text-slate-400 text-sm text-center py-8">Loading audit entries…</div>
      ) : entries.length === 0 ? (
        <div
          data-testid="audit-empty"
          className="bg-white border border-slate-200 rounded-xl py-12 text-center text-slate-400 text-sm"
        >
          No audit entries {hasActiveFilter ? 'match your filters' : 'yet'}.
        </div>
      ) : (
        <div className="space-y-4" data-testid="audit-list">
          {grouped.map(([day, dayEntries]) => (
            <div key={day}>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 px-1">
                {new Date(day).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
              </div>
              <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
                {dayEntries.map((entry) => (
                  <div key={entry.id} className="p-3 hover:bg-slate-50/50 transition">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                            style={{
                              backgroundColor: entry.source === 'automation_campaigns' ? '#DBEAFE' : '#DCFCE7',
                              color: entry.source === 'automation_campaigns' ? '#1E40AF' : '#166534',
                            }}
                          >
                            {SOURCE_LABELS[entry.source] || entry.source}
                          </span>
                          {entry.revert && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wider">
                              Revert
                            </span>
                          )}
                          {entry.reverted && (
                            <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] font-semibold uppercase tracking-wider">
                              Reverted
                            </span>
                          )}
                          {entry.row_summary && (
                            <span className="text-xs font-medium text-slate-700 truncate">
                              {entry.row_summary}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          <span className="font-medium text-slate-700">
                            {entry.user_name || entry.user_email || 'Unknown'}
                          </span>
                          {entry.user_role && (
                            <span className="ml-1.5 px-1 py-0.5 rounded bg-slate-100 text-slate-500 text-[9px] uppercase tracking-wider">
                              {entry.user_role}
                            </span>
                          )}
                          <span className="mx-1.5 text-slate-300">·</span>
                          <span className="tabular-nums">
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
                          </span>
                        </div>
                      </div>
                      {!entry.revert && !entry.reverted && (
                        <button
                          onClick={() => handleRevert(entry)}
                          disabled={revertingId === entry.id}
                          data-testid={`audit-revert-${entry.id}`}
                          className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded border border-amber-300 text-amber-700 bg-white hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                          title="Revert this edit"
                        >
                          <Undo2 size={12} />
                          {revertingId === entry.id ? 'Reverting…' : 'Revert'}
                        </button>
                      )}
                    </div>
                    <div className="space-y-0.5 pl-1">
                      {(entry.changes || []).map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <span className="font-medium text-slate-600 min-w-[90px] capitalize truncate">
                            {String(c.field).replace(/_/g, ' ')}
                          </span>
                          <span className="text-red-500 line-through tabular-nums truncate max-w-[140px]">
                            {formatValue(c.before)}
                          </span>
                          <span className="text-slate-400">→</span>
                          <span className="text-teal-700 font-semibold tabular-nums truncate max-w-[140px]">
                            {formatValue(c.after)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditLogView;
